/* ==========================================================================
   Stripling's Store Locator  (store-locator.js)
   Progressive enhancement:
     1. Renders the store LIST immediately (works with zero JS map deps).
     2. When the Google Maps JS API is present, adds the interactive map,
        custom pins, InfoWindows, pin<->card sync, fitBounds.
     3. Adds Places Autocomplete + "use my location" geolocation with a
        nearest-first distance sort.
   Data comes from a <script type="application/json" data-ssl-data> block
   inside each .ssl root (the Shopify section builds it from editable blocks),
   or from an external JSON url via data-ssl-json-url.
   Google Maps script must call window.__sslMapsReady as its callback.

   s269 changes, for the ~1,000-location wholesale where-to-buy network:
     - data-ssl-json-url is now actually FETCHED. It was read into a variable
       and never used, so the bulk path could never have loaded any data.
     - Pins are the real round Stripling's logo (asset urls passed from Liquid).
     - Click anywhere on the map to re-sort from that point.
     - Markers are culled to the viewport and the list is capped, because
       1,000 live Marker objects plus 1,000 cards makes the page unusable.
   ========================================================================== */
(function () {
  'use strict';

  var booted = false;
  var roots = [];

  var LIST_CAP = 50;     // cards rendered at once (DOM cost)
  var MARKER_CAP = 300;  // markers alive at once (map cost)

  /* ---------- small helpers ---------- */
  function $(sel, ctx) { return (ctx || document).querySelector(sel); }
  function toNum(v) { var n = parseFloat(v); return isFinite(n) ? n : null; }
  function telHref(p) { return 'tel:' + String(p || '').replace(/[^0-9+]/g, ''); }
  function esc(s) { var d = document.createElement('div'); d.textContent = s == null ? '' : String(s); return d.innerHTML; }

  /* Every field below is escaped through esc() before it reaches innerHTML.
     safeUrl is the extra guard on the one value that becomes an href: a
     store_url of "javascript:..." would survive escaping, so only same-site
     relative paths and http(s) are allowed through. */
  function safeUrl(u) {
    var s = String(u == null ? '' : u).trim();
    if (!s) return '';
    if (/^\//.test(s) && !/^\/\//.test(s)) return s;
    return /^https?:\/\//i.test(s) ? s : '';
  }

  function directionsUrl(store) {
    if (store.lat != null && store.lng != null) {
      return 'https://www.google.com/maps/dir/?api=1&destination=' + store.lat + ',' + store.lng;
    }
    return 'https://www.google.com/maps/dir/?api=1&destination=' + encodeURIComponent(store.address || '');
  }

  function haversineMiles(aLat, aLng, bLat, bLng) {
    var R = 3958.8;
    var dLat = (bLat - aLat) * Math.PI / 180;
    var dLng = (bLng - aLng) * Math.PI / 180;
    var s1 = Math.sin(dLat / 2), s2 = Math.sin(dLng / 2);
    var x = s1 * s1 + Math.cos(aLat * Math.PI / 180) * Math.cos(bLat * Math.PI / 180) * s2 * s2;
    return R * 2 * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x));
  }

  /* ---------- hours parsing (store local time = America/New_York / ET) ---------- */
  function parseTime(str) {
    var m = String(str).trim().toLowerCase().match(/^(\d{1,2})(?::(\d{2}))?\s*(am|pm)$/);
    if (!m) return null;
    var h = parseInt(m[1], 10), min = m[2] ? parseInt(m[2], 10) : 0, ap = m[3];
    if (ap === 'pm' && h !== 12) h += 12;
    if (ap === 'am' && h === 12) h = 0;
    return h * 60 + min;
  }
  function parseRange(str) {
    if (!str) return null;
    var parts = String(str).split('-');
    if (parts.length !== 2) return null;
    var open = parseTime(parts[0]), close = parseTime(parts[1]);
    if (open == null || close == null) return null;
    return { open: open, close: close };
  }
  function nowET() {
    try {
      var f = new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/New_York', weekday: 'short', hour: 'numeric', minute: 'numeric', hour12: false
      });
      var parts = {}; f.formatToParts(new Date()).forEach(function (p) { parts[p.type] = p.value; });
      var wdMap = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
      var hr = parseInt(parts.hour, 10); if (hr === 24) hr = 0;
      return { dow: wdMap[parts.weekday], minutes: hr * 60 + parseInt(parts.minute, 10) };
    } catch (e) {
      var d = new Date();
      return { dow: d.getDay(), minutes: d.getHours() * 60 + d.getMinutes() };
    }
  }
  function fmtClock(mins) {
    var h = Math.floor(mins / 60), m = mins % 60, ap = h >= 12 ? 'PM' : 'AM';
    var hh = h % 12; if (hh === 0) hh = 12;
    return hh + (m ? ':' + (m < 10 ? '0' + m : m) : '') + ' ' + ap;
  }
  function openState(store) {
    var t = nowET();
    var rangeStr = (t.dow === 0) ? store.hours_sun : store.hours_weekday;
    var r = parseRange(rangeStr);
    if (!r) return null;
    var isOpen = t.minutes >= r.open && t.minutes < r.close;
    if (isOpen) return { open: true, label: 'Open now · until ' + fmtClock(r.close) };
    if (t.minutes < r.open) return { open: false, label: 'Opens ' + fmtClock(r.open) };
    return { open: false, label: 'Closed · opens ' + fmtClock(r.open) };
  }
  function hoursText(store) {
    var wk = store.hours_weekday ? 'Mon–Sat ' + store.hours_weekday : '';
    var su = store.hours_sun ? 'Sun ' + store.hours_sun : '';
    return [wk, su].filter(Boolean).join(' · ');
  }

  /* ---------- inline icons ---------- */
  var IC_DIR = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="3 11 22 2 13 21 11 13 3 11"/></svg>';
  var IC_CALL = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.9.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z"/></svg>';
  var IC_STORE = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 9l1-5h16l1 5"/><path d="M4 9v11h16V9"/><path d="M9 20v-6h6v6"/></svg>';

  /* ---------- marker icons: the real round Stripling's logo ----------
     Asset urls come from Liquid (asset_url) via data attributes on the root.
     If they are missing we fall back to the old drawn teardrop so the map
     still works rather than rendering pinless. */
  function drawnFallback(active, flagship) {
    var fill = active ? '#e08f00' : '#301818';
    var ring = flagship ? '#90c0d8' : '#ffffff';
    var svg =
      '<svg xmlns="http://www.w3.org/2000/svg" width="40" height="52" viewBox="0 0 40 52">' +
      '<path d="M20 2C11.2 2 4 9.1 4 17.8 4 29.2 20 50 20 50s16-20.8 16-32.2C36 9.1 28.8 2 20 2z" ' +
        'fill="' + fill + '" stroke="' + ring + '" stroke-width="2.5"/>' +
      '<circle cx="20" cy="18" r="6.2" fill="#fff"/></svg>';
    return 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg);
  }

  /* The logo pin marks a STRIPLING'S STORE and nothing else (s269). A retailer that
     stocks the brand is not a Stripling's, and giving all 113 the logo made the four
     actual stores impossible to find on their own map. Stockists get a plain brand
     dot; clusters keep their count bubble. */
  function stockistIcon(active) {
    var r = active ? 11 : 8, d = r * 2 + 6, c = d / 2;
    var svg =
      '<svg xmlns="http://www.w3.org/2000/svg" width="' + d + '" height="' + d + '" viewBox="0 0 ' + d + ' ' + d + '">' +
      '<circle cx="' + c + '" cy="' + c + '" r="' + r + '" fill="' + (active ? '#E08F00' : '#301818') + '" ' +
      'stroke="#ffffff" stroke-width="2.5"/>' +
      (active ? '<circle cx="' + c + '" cy="' + c + '" r="' + (r - 5) + '" fill="#ffffff"/>' : '') +
      '</svg>';
    return {
      url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg),
      scaledSize: new google.maps.Size(d, d),
      anchor: new google.maps.Point(c, c)   // a dot is centred, not tip-anchored
    };
  }

  function iconFor(R, store, active) {
    if (!store.flagship) return stockistIcon(active);

    var url, w, h;
    if (active && R.pinActive) { url = R.pinActive; w = 44; h = 58; }
    else if (R.pinFlagship) { url = R.pinFlagship; w = 38; h = 50; }
    else if (R.pin) { url = R.pin; w = 34; h = 45; }

    if (!url) {
      url = drawnFallback(active, store.flagship);
      w = active ? 44 : 34; h = active ? 58 : 45;
    }
    return {
      url: url,
      scaledSize: new google.maps.Size(w, h),
      anchor: new google.maps.Point(w / 2, h),   // tip of the teardrop sits on the coordinate
      labelOrigin: new google.maps.Point(w / 2, h * 0.38)
    };
  }

  /* s363 A10 | Saturday, September 19, 2026 at 1:10 PM ET | MODIFIED | REQUESTED | Claude Opus 5 for Lucas Shaffer.
     was: default list in record order, so a flagged own store (Perry) could fall past LIST_CAP.
     now: own stores first; Array.sort is stable, so everything else keeps its order.
     undo: delete ownFirst and its two calls. */
  function ownFirst(arr) {
    return arr.sort(function (a, b) { return (b.flagship ? 1 : 0) - (a.flagship ? 1 : 0); });
  }

  /* ---------- list rendering ---------- */
  function cardHtml(store, idx, showDist) {
    var os = openState(store);
    var openBadge = os
      ? '<span class="ssl-card__open ' + (os.open ? 'is-open' : 'is-closed') + '">' + esc(os.label) + '</span>'
      : '';
    var dist = (showDist && typeof store._dist === 'number')
      ? '<span class="ssl-card__dist">' + store._dist.toFixed(1) + ' mi</span>' : '';
    var badge = store.flagship ? '<span class="ssl-card__badge">Stripling\'s store</span>' : '';
    var su = safeUrl(store.store_url);
    var storeLink = su
      ? '<a class="ssl-card__link ssl-card__link--store" href="' + esc(su) + '">' + IC_STORE + 'View store</a>' : '';
    var callLink = store.phone
      ? '<a class="ssl-card__link ssl-card__link--call" href="' + telHref(store.phone) + '">' + IC_CALL + esc(store.phone) + '</a>' : '';
    var hrs = hoursText(store);
    var hoursRow = (openBadge || hrs)
      ? '<div class="ssl-card__hours">' + openBadge + '<span>' + esc(hrs) + '</span></div>' : '';

    return '' +
      '<li class="ssl-card" data-ssl-idx="' + idx + '" tabindex="0" role="button" aria-label="Show ' + esc(store.name) + ' on the map">' +
        '<div class="ssl-card__head">' +
          '<div><h3 class="ssl-card__name">' + esc(store.name) + '</h3></div>' +
          (dist || badge) +
        '</div>' +
        '<p class="ssl-card__addr">' + esc(store.address) + '</p>' +
        hoursRow +
        '<div class="ssl-card__actions">' +
          '<a class="ssl-card__link ssl-card__link--dir" href="' + directionsUrl(store) + '" target="_blank" rel="noopener">' + IC_DIR + 'Directions</a>' +
          callLink + storeLink +
        '</div>' +
      '</li>';
  }

  function renderList(R, showDist) {
    var list = R.stores;
    if (!list.length) {
      R.listEl.innerHTML = '';
      R.emptyEl.hidden = false;
      R.countEl.textContent = '';
      return;
    }
    R.emptyEl.hidden = true;
    var shown = list.slice(0, LIST_CAP);
    R.countEl.textContent = list.length === shown.length
      ? list.length + (list.length === 1 ? ' location' : ' locations')
      : 'Showing ' + shown.length + ' of ' + list.length + ' locations';
    R.listEl.innerHTML = shown.map(function (s) { return cardHtml(s, s._idx, showDist); }).join('');
  }

  function setStatus(R, msg, near) {
    R.statusEl.textContent = msg || '';
    R.statusEl.className = 'ssl__status' + (near ? ' ssl__status--near' : '');
    if (near && msg) R.statusEl.innerHTML = msg;
  }

  /* ---------- clustering ----------
     At national zoom a thousand pins stack into an unreadable brown blob, so
     nearby stores collapse into a count bubble that splits as you zoom in.
     Grid-based and self-contained on purpose: a CDN clusterer library would be
     a third-party script on the client's storefront for something this small. */
  var CELL = 58; // world-pixel grid size at the current zoom

  function clusterIcon(count) {
    var r = count < 10 ? 20 : count < 100 ? 24 : 28;
    var d = r * 2;
    var fs = count < 10 ? 15 : count < 100 ? 15 : 13;
    var svg =
      '<svg xmlns="http://www.w3.org/2000/svg" width="' + d + '" height="' + d + '" viewBox="0 0 ' + d + ' ' + d + '">' +
      '<circle cx="' + r + '" cy="' + r + '" r="' + (r - 1) + '" fill="#301818" opacity="0.92"/>' +
      '<circle cx="' + r + '" cy="' + r + '" r="' + (r - 4) + '" fill="none" stroke="#F0A848" stroke-width="2.5"/>' +
      '<text x="' + r + '" y="' + (r + fs * 0.35) + '" text-anchor="middle" font-family="Helvetica,Arial,sans-serif" ' +
      'font-size="' + fs + '" font-weight="bold" fill="#ffffff">' + count + '</text></svg>';
    return {
      url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg),
      scaledSize: new google.maps.Size(d, d),
      anchor: new google.maps.Point(r, r)
    };
  }

  function worldPixel(R, lat, lng) {
    var proj = R.map.getProjection();
    if (!proj) return null;
    var p = proj.fromLatLngToPoint(new google.maps.LatLng(lat, lng));
    var scale = Math.pow(2, R.map.getZoom());
    return { x: p.x * scale, y: p.y * scale };
  }

  function clearClusters(R) {
    (R.clusters || []).forEach(function (m) { m.setMap(null); });
    R.clusters = [];
  }

  /* ---------- markers, culled to the viewport ----------
     A thousand live Marker objects will stall the map on a phone, so markers
     exist only for what is on screen (nearest-first within the viewport). */
  /* ---------- hover tooltip ----------
     Hovering any pin shows its details plus a directions link, without needing
     a click. The tooltip is hoverable itself (a short close delay, cancelled on
     enter) because otherwise the Directions link is unreachable: moving the
     cursor toward it leaves the marker and would close the card. */
  var TIP_CLOSE_MS = 220;

  function overlayProjection(R) {
    if (!R.overlay) {
      var ov = new google.maps.OverlayView();
      ov.onAdd = ov.draw = ov.onRemove = function () {};
      ov.setMap(R.map);
      R.overlay = ov;
    }
    return R.overlay.getProjection();
  }

  function ensureTip(R) {
    if (R.tipEl) return R.tipEl;
    var el = document.createElement('div');
    el.className = 'ssl__tip';
    el.setAttribute('hidden', '');
    el.addEventListener('mouseenter', function () { clearTimeout(R.tipTimer); });
    el.addEventListener('mouseleave', function () { hideTip(R); });
    R.mapEl.appendChild(el);
    R.tipEl = el;
    return el;
  }

  function hideTip(R, now) {
    clearTimeout(R.tipTimer);
    var go = function () { if (R.tipEl) { R.tipEl.setAttribute('hidden', ''); } R.tipStore = null; };
    if (now) go(); else R.tipTimer = setTimeout(go, TIP_CLOSE_MS);
  }

  function showTip(R, store) {
    if (!R.map || store.lat == null) return;
    clearTimeout(R.tipTimer);
    var proj = overlayProjection(R);
    if (!proj) return;
    var pt = proj.fromLatLngToContainerPixel(new google.maps.LatLng(store.lat, store.lng));
    if (!pt) return;

    var el = ensureTip(R);
    var os = openState(store);
    var hrs = hoursText(store);
    el.innerHTML =
      '<p class="ssl__tip-name">' + esc(store.name) + '</p>' +
      '<p class="ssl__tip-addr">' + esc(store.address) + '</p>' +
      ((os || hrs) ? '<p class="ssl__tip-meta">' + (os ? esc(os.label) + (hrs ? ' · ' : '') : '') + esc(hrs) + '</p>' : '') +
      '<div class="ssl__tip-actions">' +
        '<a class="ssl__tip-dir" href="' + directionsUrl(store) + '" target="_blank" rel="noopener">' + IC_DIR + 'Get directions</a>' +
        (store.phone ? '<a class="ssl__tip-call" href="' + telHref(store.phone) + '">' + esc(store.phone) + '</a>' : '') +
      '</div>';
    el.removeAttribute('hidden');

    // Place above the pin, and flip below if it would clip the top edge.
    var mw = R.mapEl.clientWidth, mh = R.mapEl.clientHeight;
    var tw = el.offsetWidth, th = el.offsetHeight;
    var left = Math.max(8, Math.min(pt.x - tw / 2, mw - tw - 8));
    var top = pt.y - th - 52;
    var below = top < 8;
    if (below) top = Math.min(pt.y + 14, mh - th - 8);
    el.style.left = left + 'px';
    el.style.top = top + 'px';
    el.classList.toggle('is-below', below);
    R.tipStore = store;
  }

  function ensureMarker(R, store) {
    if (store._marker) return store._marker;
    var m = new google.maps.Marker({
      position: { lat: store.lat, lng: store.lng },
      title: store.name,
      icon: iconFor(R, store, false),
      // Optimized markers are composited into a shared canvas, which makes
      // per-marker hover hit-testing unreliable for custom icons. We cap live
      // markers at MARKER_CAP anyway, so the cost of real DOM elements is
      // bounded and the hover tooltip becomes dependable.
      optimized: false
    });
    m.addListener('click', function () { selectStore(R, store); });
    store._marker = m;
    return m;
  }

  /* Hover detection is done OURSELVES against the map container rather than via
     marker 'mouseover'. Google paints a transparent hit-target layer above the
     marker images and routes events internally, so elementFromPoint over a pin
     returns transparent.png and the marker event never reaches us (verified
     s269). Projecting each visible marker back to container pixels and testing
     the cursor against its icon box is fully under our control and testable. */
  /* Returns the icon's pixel box AND how it sits on the coordinate. A Stripling's
     store is a teardrop anchored by its tip, a stockist is a dot centred on the
     point, so hit-testing has to know which it is or the dots become unhoverable. */
  function iconBox(R, store) {
    var active = store === R.active;
    if (!store.flagship) {
      var d = (active ? 11 : 8) * 2 + 6;
      return { w: d, h: d, centred: true };
    }
    if (active) return { w: 44, h: 58, centred: false };
    return { w: 38, h: 50, centred: false };
  }

  function hitTest(R, mx, my) {
    var proj = overlayProjection(R);
    if (!proj) return null;
    var best = null, bestD = Infinity;
    for (var i = 0; i < R.stores.length; i++) {
      var s = R.stores[i];
      if (!s._marker || !s._marker.getMap() || s.lat == null) continue;
      var pt = proj.fromLatLngToContainerPixel(new google.maps.LatLng(s.lat, s.lng));
      if (!pt) continue;
      var b = iconBox(R, s);
      var top = b.centred ? pt.y - b.h / 2 : pt.y - b.h;
      var bot = b.centred ? pt.y + b.h / 2 : pt.y;
      // A dot is small, so give it a few forgiving pixels or it is fiddly to hover.
      var pad = b.centred ? 4 : 0;
      if (mx >= pt.x - b.w / 2 - pad && mx <= pt.x + b.w / 2 + pad &&
          my >= top - pad && my <= bot + pad) {
        var cy = b.centred ? pt.y : pt.y - b.h * 0.65;
        var dist = Math.abs(mx - pt.x) + Math.abs(my - cy);
        if (dist < bestD) { bestD = dist; best = s; }
      }
    }
    return best;
  }

  function wireMapHover(R) {
    if (R.hoverWired) return;
    R.hoverWired = true;
    var queued = false, lastX = 0, lastY = 0;
    R.mapEl.addEventListener('mousemove', function (e) {
      var rect = R.mapEl.getBoundingClientRect();
      lastX = e.clientX - rect.left;
      lastY = e.clientY - rect.top;
      if (queued) return;
      queued = true;
      requestAnimationFrame(function () {
        queued = false;
        if (R.tipEl && !R.tipEl.hasAttribute('hidden')) {
          // Let the cursor travel onto the card to reach Get directions.
          var t = R.tipEl.getBoundingClientRect(), m = R.mapEl.getBoundingClientRect();
          if (lastX >= t.left - m.left && lastX <= t.right - m.left &&
              lastY >= t.top - m.top && lastY <= t.bottom - m.top) return;
        }
        var hit = hitTest(R, lastX, lastY);
        if (hit) { if (R.tipStore !== hit) showTip(R, hit); }
        else if (R.tipStore) hideTip(R);
      });
    });
    R.mapEl.addEventListener('mouseleave', function () { hideTip(R); });
  }

  function syncMarkers(R) {
    if (!R.map) return;
    var b = R.map.getBounds();
    if (!b) return;

    var inView = [];
    for (var i = 0; i < R.stores.length; i++) {
      var s = R.stores[i];
      if (s.lat == null || s.lng == null) continue;
      if (b.contains({ lat: s.lat, lng: s.lng })) inView.push(s);
    }
    // Keep the closest-to-center subset if the viewport holds more than we draw.
    if (inView.length > MARKER_CAP) {
      var c = R.map.getCenter(), cl = c.lat(), cg = c.lng();
      inView.sort(function (a, b2) {
        return haversineMiles(cl, cg, a.lat, a.lng) - haversineMiles(cl, cg, b2.lat, b2.lng);
      });
      inView = inView.slice(0, MARKER_CAP);
    }

    clearClusters(R);

    // Bucket the visible stores into a screen-pixel grid.
    var cells = {}, ungrouped = [], probe = inView.length ? worldPixel(R, inView[0].lat, inView[0].lng) : null;
    if (probe) {
      inView.forEach(function (s) {
        var p = worldPixel(R, s.lat, s.lng);
        if (!p) { ungrouped.push(s); return; }
        var k = Math.floor(p.x / CELL) + ':' + Math.floor(p.y / CELL);
        (cells[k] || (cells[k] = [])).push(s);
      });
    } else {
      ungrouped = inView;
    }

    var singles = ungrouped.slice();
    Object.keys(cells).forEach(function (k) {
      var group = cells[k];
      // The active store always stays a real pin so the InfoWindow has an anchor.
      if (group.length === 1 || group.indexOf(R.active) !== -1) {
        singles = singles.concat(group);
        return;
      }
      var lat = 0, lng = 0;
      group.forEach(function (s) { lat += s.lat; lng += s.lng; });
      var cm = new google.maps.Marker({
        position: { lat: lat / group.length, lng: lng / group.length },
        map: R.map,
        icon: clusterIcon(group.length),
        title: group.length + ' locations',
        zIndex: 1
      });
      cm.addListener('click', function () {
        var gb = new google.maps.LatLngBounds();
        group.forEach(function (s) { gb.extend({ lat: s.lat, lng: s.lng }); });
        // A cluster of identical coordinates has a zero-area bounds, which
        // fitBounds would zoom to maximum. Step in instead.
        if (gb.getNorthEast().equals(gb.getSouthWest())) {
          R.map.setCenter(gb.getCenter());
          R.map.setZoom(Math.min(17, R.map.getZoom() + 3));
        } else {
          R.map.fitBounds(gb, 60);
        }
      });
      R.clusters.push(cm);
    });

    var keep = {};
    singles.forEach(function (s) {
      var m = ensureMarker(R, s);
      if (m.getMap() !== R.map) m.setMap(R.map);
      keep[s._idx] = true;
    });
    R.stores.forEach(function (s) {
      if (!s._marker) return;
      if (!keep[s._idx] && s !== R.active && s._marker.getMap()) s._marker.setMap(null);
    });

    R.shownCount = inView.length;
  }

  function buildMap(R) {
    if (!(window.google && google.maps) || R.mapReady) return;
    var mapEl = R.mapEl;
    mapEl.innerHTML = '';
    R.map = new google.maps.Map(mapEl, {
      center: { lat: R.center.lat, lng: R.center.lng },
      zoom: R.zoom,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: true,
      gestureHandling: 'cooperative',
      clickableIcons: false,
      styles: [
        { featureType: 'poi.business', stylers: [{ visibility: 'off' }] },
        { featureType: 'poi.park', elementType: 'labels.text', stylers: [{ visibility: 'off' }] },
        { featureType: 'transit', stylers: [{ visibility: 'off' }] },
        { featureType: 'water', elementType: 'geometry.fill', stylers: [{ color: '#cfe8f2' }] },
        { featureType: 'road.highway', elementType: 'geometry.fill', stylers: [{ color: '#ffd98a' }] }
      ]
    });
    R.info = new google.maps.InfoWindow({ maxWidth: 260 });

    // CLICK THE MAP to re-sort from that spot (s269).
    R.map.addListener('click', function (e) {
      if (!e || !e.latLng) return;
      focusNear(R, e.latLng.lat(), e.latLng.lng(), 'that spot', true);
    });

    // Viewport-driven marker rendering.
    R.map.addListener('idle', function () { syncMarkers(R); });

    // A tooltip anchored to a pixel goes stale the moment the map moves, so
    // drop it immediately on any pan or zoom rather than letting it float.
    ['dragstart', 'zoom_changed', 'center_changed'].forEach(function (ev) {
      R.map.addListener(ev, function () { if (R.tipStore) hideTip(R, true); });
    });

    wireMapHover(R);

    fitAll(R);
    R.mapReady = true;
    setupPlaces(R);
  }

  function fitAll(R) {
    var pts = R.stores.filter(function (s) { return s.lat != null && s.lng != null; });
    if (!pts.length) return;
    var b = new google.maps.LatLngBounds();
    pts.forEach(function (s) { b.extend({ lat: s.lat, lng: s.lng }); });
    R.map.fitBounds(b, 40);
    google.maps.event.addListenerOnce(R.map, 'idle', function () {
      if (R.map.getZoom() > 13) R.map.setZoom(13);
      syncMarkers(R);
    });
  }

  function infoHtml(store) {
    var os = openState(store);
    var hrs = hoursText(store);
    return '<div class="ssl-info">' +
      '<p class="ssl-info__name">' + esc(store.name) + '</p>' +
      '<p class="ssl-info__addr">' + esc(store.address) + '</p>' +
      ((os || hrs) ? '<p class="ssl-info__hours">' + (os ? esc(os.label) + ' · ' : '') + esc(hrs) + '</p>' : '') +
      '<div class="ssl-info__links">' +
        '<a href="' + directionsUrl(store) + '" target="_blank" rel="noopener">Directions</a>' +
        (store.phone ? '<a href="' + telHref(store.phone) + '">Call</a>' : '') +
        (safeUrl(store.store_url) ? '<a href="' + esc(safeUrl(store.store_url)) + '">View store</a>' : '') +
      '</div></div>';
  }

  function highlightCard(R, store) {
    var prev = R.listEl.querySelectorAll('.ssl-card.is-active');
    for (var i = 0; i < prev.length; i++) prev[i].classList.remove('is-active');
    var card = R.listEl.querySelector('.ssl-card[data-ssl-idx="' + store._idx + '"]');
    if (card) {
      card.classList.add('is-active');
      card.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }

  function selectStore(R, store) {
    if (R.map && store.lat != null) {
      var prev = R.active;
      R.active = store;
      if (prev && prev._marker) prev._marker.setIcon(iconFor(R, prev, false));
      var m = ensureMarker(R, store);
      if (m.getMap() !== R.map) m.setMap(R.map);
      m.setIcon(iconFor(R, store, true));
      m.setZIndex(998);
      R.info.setContent(infoHtml(store));
      R.info.open(R.map, m);
      R.map.panTo(m.getPosition());
      if (R.map.getZoom() < 11) R.map.setZoom(12);
    }
    highlightCard(R, store);
  }

  /* ---------- nearest / search ---------- */
  function focusNear(R, lat, lng, label, fromMapClick) {
    R.stores.forEach(function (s) {
      s._dist = (s.lat != null && s.lng != null) ? haversineMiles(lat, lng, s.lat, s.lng) : null;
    });
    R.stores.sort(function (a, b) {
      if (a._dist == null) return 1; if (b._dist == null) return -1; return a._dist - b._dist;
    });
    renderList(R, true);

    if (R.map) {
      if (R.userMarker) R.userMarker.setMap(null);
      R.userMarker = new google.maps.Marker({
        position: { lat: lat, lng: lng }, map: R.map, title: label || 'Your location', zIndex: 999,
        icon: {
          path: google.maps.SymbolPath.CIRCLE, scale: 8,
          fillColor: '#2b6cb0', fillOpacity: 1, strokeColor: '#fff', strokeWeight: 3
        }
      });
      var b = new google.maps.LatLngBounds();
      b.extend({ lat: lat, lng: lng });
      R.stores.filter(function (s) { return s.lat != null; }).slice(0, 3)
        .forEach(function (s) { b.extend({ lat: s.lat, lng: s.lng }); });
      R.map.fitBounds(b, 70);
      google.maps.event.addListenerOnce(R.map, 'idle', function () { syncMarkers(R); });
    }

    var top = R.stores.filter(function (s) { return s._dist != null; })[0];
    if (top) {
      var lead = fromMapClick ? 'Nearest to that spot: ' : 'Your nearest store: ';
      setStatus(R, lead + '<strong>' + esc(top.name) + '</strong> (' + top._dist.toFixed(1) + ' mi away)', true);
      if (R.map) selectStore(R, top);
    } else {
      setStatus(R, 'Sorted by distance from ' + (label || 'your location') + '.');
    }
  }

  function setupPlaces(R) {
    if (!(window.google && google.maps && google.maps.places) || R.placesReady) return;
    R.placesReady = true;
    try {
      var ac = new google.maps.places.Autocomplete(R.inputEl, {
        fields: ['geometry', 'name', 'formatted_address'],
        componentRestrictions: { country: 'us' },
        types: ['geocode']
      });
      ac.addListener('place_changed', function () {
        var place = ac.getPlace();
        if (place && place.geometry && place.geometry.location) {
          var loc = place.geometry.location;
          focusNear(R, loc.lat(), loc.lng(), place.name || place.formatted_address);
        }
      });
      R.autocomplete = ac;
    } catch (e) { /* Places not enabled; the submit-geocode fallback still works */ }
  }

  function geocodeSubmit(R, q) {
    if (!(window.google && google.maps && google.maps.Geocoder)) {
      setStatus(R, 'Start typing your city or ZIP and pick a suggestion.');
      return;
    }
    setStatus(R, 'Searching…');
    new google.maps.Geocoder().geocode(
      { address: q, componentRestrictions: { country: 'US' } },
      function (res, status) {
        if (status === 'OK' && res[0]) {
          var loc = res[0].geometry.location;
          focusNear(R, loc.lat(), loc.lng(), res[0].formatted_address);
        } else {
          setStatus(R, 'We could not find "' + q + '". Try a ZIP code or "City, ST".');
        }
      }
    );
  }

  function geolocate(R) {
    if (!navigator.geolocation) { setStatus(R, 'Location is not available on this device. Type your ZIP or city instead.'); return; }
    setStatus(R, 'Finding your location…');
    navigator.geolocation.getCurrentPosition(
      function (pos) { focusNear(R, pos.coords.latitude, pos.coords.longitude, 'your location'); },
      function () { setStatus(R, 'We could not get your location. Type your ZIP or city instead.'); },
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 300000 }
    );
  }

  /* ---------- wiring ---------- */
  function wire(R) {
    R.listEl.addEventListener('click', function (e) {
      if (e.target.closest('a')) return;
      var card = e.target.closest('.ssl-card'); if (!card) return;
      var s = R.byIdx[card.getAttribute('data-ssl-idx')];
      if (s) selectStore(R, s);
    });
    R.listEl.addEventListener('keydown', function (e) {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      var card = e.target.closest('.ssl-card'); if (!card) return;
      if (e.target.closest('a')) return;
      e.preventDefault();
      var s = R.byIdx[card.getAttribute('data-ssl-idx')];
      if (s) selectStore(R, s);
    });
    if (R.formEl) {
      R.formEl.addEventListener('submit', function (e) {
        e.preventDefault();
        var q = (R.inputEl.value || '').trim();
        if (!q) { setStatus(R, 'Type your ZIP code or city to find the nearest store.'); return; }
        geocodeSubmit(R, q);
      });
    }
    if (R.geoBtn) R.geoBtn.addEventListener('click', function () { geolocate(R); });
  }

  /* ---------- data ---------- */
  function normalize(s, i) {
    return {
      name: s.name, address: s.address, city: s.city, region: s.region, zip: s.zip,
      phone: s.phone, store_url: s.store_url, flagship: !!s.flagship,
      hours_weekday: s.hours_weekday, hours_sun: s.hours_sun,
      lat: toNum(s.lat), lng: toNum(s.lng), _idx: i
    };
  }

  function parseInline(R) {
    var dataEl = R.root.querySelector('[data-ssl-data]');
    var raw = [];
    if (dataEl) { try { raw = JSON.parse(dataEl.textContent || '[]'); } catch (e) { raw = []; } }
    return (Array.isArray(raw) ? raw : (raw.stores || [])).map(normalize);
  }

  /* The bulk path. Before s269 this url was read into a variable and never
     fetched, so a large dealer network could not load at all. */
  function loadRemote(R) {
    if (!R.jsonUrl) return;
    setStatus(R, 'Loading store list…');
    fetch(R.jsonUrl, { credentials: 'omit' })
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.json();
      })
      .then(function (data) {
        var arr = Array.isArray(data) ? data : (data.stores || data.stockists || []);
        if (!arr.length) throw new Error('empty');
        // MERGE, do not replace. The inline blocks are Stripling's OWN stores
        // (flagship pins, with hours and store pages); the remote file is the
        // where-to-buy retail network. Replacing would erase their own stores
        // from their own store map. Dedupe on name+zip so a stockist row that
        // duplicates one of their locations does not double up.
        var seen = {}, merged = [];
        R.stores.forEach(function (s) {
          seen[(s.name || '').toLowerCase() + '|' + (s.zip || '')] = true;
          merged.push(s);
        });
        arr.forEach(function (s) {
          var k = (s.name || '').toLowerCase() + '|' + (s.zip || '');
          if (seen[k]) return;
          seen[k] = true;
          merged.push(s);
        });
        R.stores = ownFirst(merged).map(function (s, i) { return normalize(s, i); });
        R.byIdx = {};
        R.stores.forEach(function (s) { R.byIdx[s._idx] = s; });
        R.active = null;
        renderList(R, false);
        setStatus(R, 'Showing all ' + R.stores.length + ' locations. Enter your ZIP or city to find the closest, or click the map.');
        if (R.map) { fitAll(R); } else if (window.google && google.maps) { buildMap(R); }
      })
      .catch(function (err) {
        // Inline blocks stay on screen, so a failed fetch degrades instead of blanking.
        setStatus(R, R.stores.length
          ? 'Showing our main locations. The full store list could not be loaded just now.'
          : 'Our store list is being updated. Please check back soon.');
        if (window.console) console.warn('[store-locator] remote list failed:', err);
      });
  }

  function initRoot(rootEl) {
    var R = {
      root: rootEl,
      mapEl: $('[data-ssl-map]', rootEl),
      listEl: $('[data-ssl-list]', rootEl),
      emptyEl: $('[data-ssl-empty]', rootEl),
      countEl: $('[data-ssl-count]', rootEl),
      statusEl: $('[data-ssl-status]', rootEl),
      formEl: $('[data-ssl-form]', rootEl),
      inputEl: $('[data-ssl-input]', rootEl),
      geoBtn: $('[data-ssl-geo]', rootEl),
      jsonUrl: rootEl.getAttribute('data-ssl-json-url') || '',
      pin: rootEl.getAttribute('data-ssl-pin') || '',
      pinActive: rootEl.getAttribute('data-ssl-pin-active') || '',
      pinFlagship: rootEl.getAttribute('data-ssl-pin-flagship') || '',
      center: { lat: toNum(rootEl.getAttribute('data-ssl-center-lat')) || 39.5, lng: toNum(rootEl.getAttribute('data-ssl-center-lng')) || -98.35 },
      zoom: parseInt(rootEl.getAttribute('data-ssl-zoom'), 10) || 7,
      mapReady: false, placesReady: false, active: null
    };
    R.stores = ownFirst(parseInline(R));
    R.byIdx = {};
    R.stores.forEach(function (s) { R.byIdx[s._idx] = s; });
    renderList(R, false);
    wire(R);
    if (R.stores.length) {
      setStatus(R, 'Showing all ' + R.stores.length + ' locations. Enter your ZIP or city to find the closest, or click the map.');
    }
    roots.push(R);
    if (window.google && google.maps) buildMap(R);
    loadRemote(R);
    return R;
  }

  function initAllMaps() { roots.forEach(function (R) { buildMap(R); }); }

  function boot() {
    if (booted) return; booted = true;
    var found = document.querySelectorAll('.ssl[data-ssl-root]');
    for (var i = 0; i < found.length; i++) { if (!found[i].__sslInit) { found[i].__sslInit = true; initRoot(found[i]); } }
    if (window.__sslGmapsReady) initAllMaps();
  }

  window.__sslMapsReady = function () { window.__sslGmapsReady = true; if (booted) initAllMaps(); };

  if (document.readyState !== 'loading') boot();
  else document.addEventListener('DOMContentLoaded', boot);
})();
